<template>
  <div
    :class="{ 'custom-error': validator && validator.$error, 'admin-editor-localized': isAdminLocalization }"
    :style="adminEditorLabels"
  >
    <vue-editor
      :id="getUID()"
      :value="value"
      :editor-toolbar="editorToolbar"
      :placeholder="placeholder"
      :disabled="disabled"
      :use-custom-image-handler="true"
      @image-added="handleImageAdded"
      @input="
        $emit('input', $event)
        validator && validator.$touch()
      "
      @blur="validator && validator.$touch()"
    />
    <div :class="['custom-error-message', { 'error--text': validator && validator.$error }]">
      <span> {{ getErrors(validator)[0] }} &nbsp;</span>
    </div>
  </div>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import { VueEditor } from 'vue2-editor'

import {
  defaultMessages,
  getUploadOptions,
  getValidationErrors,
  uploadImage,
} from './../../../utils'
import { UploadPaths } from '@/types'

const toolbar = [
  ['bold', 'italic', 'underline', 'strike', { size: ['small', false, 'large', 'huge'] }],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
]

export default {
  mixins: [AdminContext],
  name: 'text-editor-field',
  components: {
    VueEditor,
  },
  props: {
    validator: {
      type: Object,
      default: null,
    },
    errorMessages: {
      type: Object,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    imageAndCodeBlockEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editorToolbar: this.imageAndCodeBlockEnabled ? toolbar.concat([['image', 'video']]) : toolbar,
      imageUploadLoading: false,
    }
  },
  computed: {
    adminEditorLabels() {
      if (!this.isAdminLocalization) return {}

      // Quill Snow renders these labels through CSS pseudo-elements. Per-editor
      // variables update their display without touching editor contents or globals.
      return {
        '--admin-editor-normal': JSON.stringify(this.adminText('editor.normal', 'Normal')),
        '--admin-editor-small': JSON.stringify(this.adminText('editor.small', 'Small')),
        '--admin-editor-large': JSON.stringify(this.adminText('editor.large', 'Large')),
        '--admin-editor-huge': JSON.stringify(this.adminText('editor.huge', 'Huge')),
        '--admin-editor-visit': JSON.stringify(this.adminText('editor.visit', 'Visit URL:')),
        '--admin-editor-edit': JSON.stringify(this.adminText('editor.edit', 'Edit')),
        '--admin-editor-remove': JSON.stringify(this.adminText('editor.remove', 'Remove')),
        '--admin-editor-save': JSON.stringify(this.adminText('editor.save', 'Save')),
        '--admin-editor-link': JSON.stringify(this.adminText('editor.link', 'Enter link:')),
        '--admin-editor-formula': JSON.stringify(this.adminText('editor.formula', 'Enter formula:')),
        '--admin-editor-video': JSON.stringify(this.adminText('editor.video', 'Enter video:')),
      }
    },
  },
  methods: {
    getErrors: function (field) {
      return getValidationErrors({
        ...this.adminValidationMessages(defaultMessages),
        ...this.adminFieldValidationMessages(this.errorMessages),
      })(field)
    },
    async handleImageAdded(fileImage, Editor, cursorLocation, resetUploader) {
      this.imageUploadLoading = true
      const { file, path } = await getUploadOptions(fileImage, UploadPaths.UNGROUPED, {
        resize: false,
      })
      const url = await uploadImage(file, path)
      this.imageUploadLoading = false

      Editor.insertEmbed(cursorLocation, 'image', url)
      resetUploader()
    },
  },
}
</script>

<style>
.ql-container {
  border: 0px !important;
}

.ql-editor {
  font-family: 'Source Sans Pro', sans-serif;
  border-bottom: 0.125rem #979797 solid !important;
}

.ql-editor:focus {
  border-bottom: 0.125rem #0277bd solid !important;
}

.ql-editor:hover {
  background-color: rgba(2, 119, 189, 0.2) !important;
}

.ql-editor::before {
  font-style: normal !important;
  color: rgba(0, 0, 0, 0.54);
}
.custom-error .ql-editor {
  border-bottom: 0.125rem #db384c solid !important;
}

.ql-toolbar {
  border: 0px !important;
  background: rgba(0, 0, 0, 0.06);
}

.ql-editor p img {
  display: block;
  max-width: 100%;
  margin: 0 auto;
}

.ql-editor iframe {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
}

/* Override only the opted-in editor; customer editors retain Quill defaults. */
.admin-editor-localized .ql-snow .ql-picker.ql-size .ql-picker-label::before,
.admin-editor-localized .ql-snow .ql-picker.ql-size .ql-picker-item::before {
  content: var(--admin-editor-normal);
}
.admin-editor-localized .ql-snow .ql-picker.ql-size .ql-picker-label[data-value=small]::before,
.admin-editor-localized .ql-snow .ql-picker.ql-size .ql-picker-item[data-value=small]::before {
  content: var(--admin-editor-small);
}
.admin-editor-localized .ql-snow .ql-picker.ql-size .ql-picker-label[data-value=large]::before,
.admin-editor-localized .ql-snow .ql-picker.ql-size .ql-picker-item[data-value=large]::before {
  content: var(--admin-editor-large);
}
.admin-editor-localized .ql-snow .ql-picker.ql-size .ql-picker-label[data-value=huge]::before,
.admin-editor-localized .ql-snow .ql-picker.ql-size .ql-picker-item[data-value=huge]::before {
  content: var(--admin-editor-huge);
}
.admin-editor-localized .ql-snow .ql-tooltip::before {
  content: var(--admin-editor-visit);
}
.admin-editor-localized .ql-snow .ql-tooltip a.ql-action::after {
  content: var(--admin-editor-edit);
}
.admin-editor-localized .ql-snow .ql-tooltip a.ql-remove::before {
  content: var(--admin-editor-remove);
}
.admin-editor-localized .ql-snow .ql-tooltip.ql-editing a.ql-action::after {
  content: var(--admin-editor-save);
}
.admin-editor-localized .ql-snow .ql-tooltip[data-mode=link]::before {
  content: var(--admin-editor-link);
}
.admin-editor-localized .ql-snow .ql-tooltip[data-mode=formula]::before {
  content: var(--admin-editor-formula);
}
.admin-editor-localized .ql-snow .ql-tooltip[data-mode=video]::before {
  content: var(--admin-editor-video);
}
</style>
